import { MutableRefObject, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'

import {
  useCurrentUser,
  useGetUserInterestsQuery,
  useUpdateUserInterestsMutation,
} from 'store/api/yay/user'
import { addNotification } from 'store/slices/notifications'

import Button from 'components/Button'
import ImageLoader from 'components/ImageLoader'

import './style.scss'

type Props = {
  isModal?: boolean
  saveButtonRef?: MutableRefObject<HTMLButtonElement>
  saveCallback?: (isSuccess: boolean) => void
}

export const InterestsSelection = ({
  isModal,
  saveButtonRef,
  saveCallback,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { data: currentUser } = useCurrentUser()
  const userInterests = useGetUserInterestsQuery({}, { skip: !currentUser?.id })
  const [updateUserInterests, { isLoading }] = useUpdateUserInterestsMutation({
    fixedCacheKey: 'updateUserInterests',
  })

  const [selected, setSelected] = useState<Set<number>>(new Set())

  const onToggle = (id: number) => {
    setSelected((prev) => {
      if (prev.has(id)) prev.delete(id)
      else prev.add(id)

      return new Set(prev)
    })
  }

  const onSave = async () => {
    if (!userInterests.isSuccess) return

    const result = await updateUserInterests(Array.from(selected))

    if ('data' in result) {
      dispatch(addNotification({ message: t('common.saved') }))

      saveCallback?.(result.data)
    }
  }

  useEffect(() => {
    if (Boolean(selected) && currentUser.interests_selected === false) {
      updateUserInterests(Array.from(selected))
    }
  }, [currentUser, Boolean(selected)])

  useEffect(() => {
    if (
      userInterests.isSuccess &&
      !userInterests.isFetching &&
      userInterests.data?.length
    ) {
      setSelected(
        new Set(
          userInterests.data
            .filter((topic) => topic.selected)
            .map((topic) => topic.id)
        )
      )
    }
  }, [userInterests])

  return (
    <div
      className={cx('InterestsSelection', {
        'InterestsSelection--modal': isModal,
      })}
    >
      <h1>{t('user_setting.interests_selection.description_title')}</h1>
      <p>{t('user_setting.interests_selection.description_message')}</p>

      <div className="InterestsSelection__body">
        {userInterests.data?.map((topic) => (
          <div
            key={topic.id}
            className={cx('InterestsSelection__body__item', {
              'InterestsSelection__body__item--selected': selected?.has(
                topic.id
              ),
            })}
            onClick={() => onToggle(topic.id)}
          >
            <img
              className="InterestsSelection__body__item__check"
              src="/images/icon-check-blue-circle.svg"
              alt="selected"
            />
            <ImageLoader
              className="InterestsSelection__body__item__icon"
              url={topic.icon}
            />
            <div className="InterestsSelection__body__item__name">
              {topic.name}
            </div>
          </div>
        ))}
      </div>

      <Button
        ref={saveButtonRef}
        onClick={() => onSave()}
        loading={isLoading}
        disabled={isLoading}
      >
        {t('common.save')}
      </Button>
    </div>
  )
}
